import React, { useState } from 'react';
import { Calendar, MapPin, Search, ExternalLink, Clock, Flag, Star } from 'lucide-react';
import { Navigation } from '../components/Navigation';

// Popular tee-time hubs — focused exclusively on booking, separate from the multi-day Trips/Travel page.
const POPULAR_AREAS = [
  { id: 'myrtle-beach',  name: 'Myrtle Beach',  state: 'SC', image: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=800&q=80' },
  { id: 'scottsdale',    name: 'Scottsdale',    state: 'AZ', image: 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=800&q=80' },
  { id: 'pinehurst',     name: 'Pinehurst',     state: 'NC', image: 'https://images.unsplash.com/photo-1593111774240-d529f12cf4bb?w=800&q=80' },
  { id: 'orlando',       name: 'Orlando',       state: 'FL', image: 'https://images.unsplash.com/photo-1601066521260-44cd0c1cf6f1?w=800&q=80' },
  { id: 'palm-springs',  name: 'Palm Springs',  state: 'CA', image: 'https://images.unsplash.com/photo-1622038200478-3a9ca58cea59?w=800&q=80' },
  { id: 'pebble-beach',  name: 'Pebble Beach',  state: 'CA', image: 'https://images.unsplash.com/photo-1600005082646-8a6f1f4f4e9f?w=800&q=80' },
];

// Generic tee-time partners — each opens in a new tab in the system browser
const PARTNERS = [
  { key: 'golfnow',    name: 'GolfNow',     blurb: 'Largest U.S. tee-time marketplace · last-minute deals', url: 'https://www.golfnow.com/' },
  { key: 'teeoff',     name: 'TeeOff',      blurb: 'PGA Tour-affiliated · over 4,000 courses', url: 'https://www.teeoff.com/' },
  { key: 'supreme',    name: 'Supreme Golf',blurb: 'Compares prices across booking sites', url: 'https://www.supremegolf.com/' },
  { key: 'chronogolf', name: 'Lightspeed Tee Times', blurb: 'Direct booking with public & resort courses', url: 'https://www.chronogolf.com/' },
];

// Build a Google search that lands on tee-time booking pages
const teeTimeSearchUrl = (q) =>
  `https://www.google.com/search?q=${encodeURIComponent(`book golf tee times ${q.trim()}`)}`;

export default function BookTeeTimes() {
  const [query, setQuery] = useState('');

  const onSearch = (e) => {
    e?.preventDefault();
    const q = query.trim();
    if (!q) return;
    window.open(teeTimeSearchUrl(q), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav">
      <Navigation />

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-2xl bg-blue-500 flex items-center justify-center shadow-md">
              <Flag className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 data-testid="book-tee-title" className="font-heading font-bold text-2xl sm:text-3xl text-bundle-green leading-tight">Book Tee Times</h1>
              <p className="text-gray-600 text-sm">Find courses &amp; book tee times anywhere in the U.S.</p>
            </div>
          </div>
        </div>

        {/* ===== City / Area search ===== */}
        <form onSubmit={onSearch} className="bg-white rounded-2xl border border-bundle-cream-dark shadow-sm p-4 mb-8" data-testid="tee-time-search-card">
          <label className="text-xs font-bold text-bundle-green uppercase tracking-wider mb-2 block">Search by city, area, or course</label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder='e.g. "Bethesda MD" or "TPC Sawgrass"'
                className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:border-bundle-green focus:bg-white"
                data-testid="tee-time-search-input"
              />
            </div>
            <button
              type="submit"
              data-testid="tee-time-search-btn"
              className="px-4 py-2.5 rounded-lg bg-bundle-green text-white text-sm font-bold hover:bg-bundle-green-dark active:scale-95 transition-all whitespace-nowrap"
            >
              Search
            </button>
          </div>
          <p className="text-[11px] text-gray-500 mt-2">Opens Google results with current tee times near your search.</p>
        </form>

        {/* ===== Booking partners ===== */}
        <h2 className="text-base font-heading font-bold text-bundle-green mb-3">Top Booking Partners</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10" data-testid="tee-time-partners">
          {PARTNERS.map(p => (
            <a
              key={p.key}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`tee-partner-${p.key}`}
              className="group flex items-start gap-3 p-4 rounded-2xl bg-white border border-bundle-cream-dark hover:border-bundle-green hover:shadow-md active:scale-[0.99] transition-all"
            >
              <div className="w-10 h-10 rounded-xl bg-bundle-green/10 group-hover:bg-bundle-green/20 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-bundle-green" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="font-bold text-bundle-green text-sm">{p.name}</p>
                  <ExternalLink className="w-3 h-3 text-gray-400 group-hover:text-bundle-green transition-colors" />
                </div>
                <p className="text-xs text-gray-500 leading-tight mt-0.5">{p.blurb}</p>
              </div>
            </a>
          ))}
        </div>

        {/* ===== Popular tee-time markets ===== */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-heading font-bold text-bundle-green">Popular Markets</h2>
          <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">Tap to search</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="tee-time-popular-markets">
          {POPULAR_AREAS.map(a => (
            <a
              key={a.id}
              href={teeTimeSearchUrl(`${a.name} ${a.state}`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`tee-market-${a.id}`}
              className="group relative overflow-hidden rounded-2xl aspect-[5/3] border border-bundle-cream-dark hover:shadow-lg transition-all"
            >
              <img
                src={a.image}
                alt={a.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <p className="text-white font-black text-sm leading-tight">{a.name}</p>
                <div className="flex items-center gap-1 text-bundle-gold text-[10px] font-bold mt-0.5">
                  <MapPin className="w-3 h-3" /> {a.state}
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* ===== Tips ===== */}
        <div className="mt-10 p-4 rounded-2xl bg-bundle-green/5 border border-bundle-green/15">
          <div className="flex items-start gap-3">
            <Star className="w-5 h-5 text-bundle-gold flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-bundle-green text-sm mb-1">Tee-Time Tips</p>
              <ul className="text-xs text-gray-700 space-y-1 list-disc pl-4">
                <li>Best rates: weekday mid-mornings &amp; twilight slots.</li>
                <li>GolfNow &amp; TeeOff often differ — check both for the same course.</li>
                <li>Looking for a multi-day trip with hotels &amp; flights? See <a href="/golf-trips" className="text-bundle-green font-bold underline">Travel</a>.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Spacer for sticky nav */}
        <div className="h-8" />
      </div>
    </div>
  );
}
